import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService, StockForecastItem, StockForecastResponse } from '../services/api';

const formatTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-PT', {
      timeZone: 'Europe/Lisbon',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return timestamp;
  }
};

const formatEur = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';
};

const formatPct = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  return value.toFixed(2) + '%';
};

const formatStockoutDays = (days: number | null): JSX.Element => {
  if (days === null || days === undefined) return <span className="text-muted">--</span>;
  const rounded = Math.round(days);
  let className = '';
  if (rounded < 7) className = 'text-danger fw-bold';
  else if (rounded < 14) className = 'fw-bold';
  else if (rounded < 30) className = 'text-warning';
  else className = 'text-success';
  return <span className={className} style={rounded >= 7 && rounded < 14 ? { color: '#fd7e14' } : undefined}>{rounded}d</span>;
};

const calcAvgStockout = (item: StockForecastItem): number | null => {
  const vals = [item.stockout_days_28d, item.stockout_days_14d, item.stockout_days_7d, item.stockout_days_3d, item.stockout_days_1d]
    .filter((v): v is number => v !== null && v !== undefined);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const calcUnitsSold = (dpd: number | null, days: number): string => {
  if (dpd === null || dpd === undefined) return '-';
  const total = dpd * days;
  return total < 1 ? total.toFixed(1) : Math.round(total).toString();
};

const StockForecastPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [forecastData, setForecastData] = useState<StockForecastItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshotTimestamp, setSnapshotTimestamp] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;

  const initialOrderBy = searchParams.get('order_by') || 'position_asc';
  const initialSearch = searchParams.get('search') || '';
  const initialZeroStock = searchParams.get('include_zero_stock') !== 'false';

  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [includeZeroStock, setIncludeZeroStock] = useState(initialZeroStock);

  const syncParams = useCallback((params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([k, v]) => {
      if (v) newParams.set(k, v);
      else newParams.delete(k);
    });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * pageSize;
      const response: StockForecastResponse = await apiService.getStockForecast({
        limit: pageSize,
        offset,
        order_by: orderBy,
        search: searchTerm || undefined,
        include_zero_stock: includeZeroStock,
      });
      setForecastData(response.results);
      setTotalCount(response.total_count);
      setSnapshotTimestamp(response.snapshot_timestamp);
    } catch (err: any) {
      console.error('Error loading stock forecast:', err);
      setError('Erro ao carregar dados. Por favor, tente novamente.');
      setForecastData([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, orderBy, searchTerm, includeZeroStock]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, orderBy, includeZeroStock]);

  const handleSkuClick = (sku: string) => {
    window.open(`/products/${sku}`, '_blank');
  };

  const handleOrderChange = (newOrder: string) => {
    setOrderBy(newOrder);
    syncParams({ order_by: newOrder });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const criticalCount = forecastData.filter(i => i.stockout_days_7d !== null && i.stockout_days_7d < 7).length;
  const warningCount = forecastData.filter(i => i.stockout_days_7d !== null && i.stockout_days_7d >= 7 && i.stockout_days_7d < 14).length;

  const exportToCSV = async () => {
    try {
      const allData = await apiService.getStockForecast({
        limit: 50000,
        offset: 0,
        order_by: orderBy,
        search: searchTerm || undefined,
        include_zero_stock: includeZeroStock,
      });

      const headers = ['Pos', 'SKU', 'Produto', 'Stock', 'Preco',
        'Media Stockout', 'Stockout 28d', 'Stockout 14d', 'Stockout 7d', 'Stockout 3d', 'Stockout 1d',
        'Vendas 28d', 'Vendas 14d', 'Vendas 7d', 'Vendas 3d', 'Vendas 1d',
        'Pot. Vendas', 'Receita 7d', '% Emp 7d', 'Receita 28d', '% Emp 28d',
        'Categoria', 'Marca'];
      const rows = allData.results.map(r => {
        const avg = calcAvgStockout(r);
        return [
          r.ranking_position ?? '',
          r.sku,
          (r.product_name || '').replace(/;/g, ','),
          r.stock_quantity,
          r.preco_atual?.toFixed(2) ?? '',
          avg !== null ? Math.round(avg) : '',
          r.stockout_days_28d ? Math.round(r.stockout_days_28d) : '',
          r.stockout_days_14d ? Math.round(r.stockout_days_14d) : '',
          r.stockout_days_7d ? Math.round(r.stockout_days_7d) : '',
          r.stockout_days_3d ? Math.round(r.stockout_days_3d) : '',
          r.stockout_days_1d ? Math.round(r.stockout_days_1d) : '',
          r.dpd_28 ? (r.dpd_28 * 28).toFixed(1) : '',
          r.dpd_14 ? (r.dpd_14 * 14).toFixed(1) : '',
          r.dpd_7 ? (r.dpd_7 * 7).toFixed(1) : '',
          r.dpd_3 ? (r.dpd_3 * 3).toFixed(1) : '',
          r.dpd_1 ?? '',
          r.sales_potential?.toFixed(2) ?? '',
          r.revenue_7d?.toFixed(2) ?? '',
          r.revenue_pct_7d?.toFixed(2) ?? '',
          r.revenue_28d?.toFixed(2) ?? '',
          r.revenue_pct_28d?.toFixed(2) ?? '',
          (r.categoria_nome || '').replace(/;/g, ','),
          (r.marca_nome || '').replace(/;/g, ','),
        ].join(';');
      });

      const csvContent = [headers.join(';'), ...rows].join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock_forecast_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>Stock Forecast</h1>
            <button className="btn btn-outline-success" onClick={exportToCSV} disabled={loading || totalCount === 0}>
              Exportar CSV
            </button>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      {!loading && !error && totalCount > 0 && (
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="card bg-primary text-white">
              <div className="card-body text-center">
                <h5 className="card-title">Produtos com Stock</h5>
                <h2>{totalCount}</h2>
                {snapshotTimestamp && (
                  <small>Snapshot: {formatTimestamp(snapshotTimestamp)}</small>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-danger text-white">
              <div className="card-body text-center">
                <h5 className="card-title">Critico (&lt; 7d)</h5>
                <h2>{criticalCount}</h2>
                <small>nesta pagina</small>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card text-white" style={{ backgroundColor: '#fd7e14' }}>
              <div className="card-body text-center">
                <h5 className="card-title">Aviso (7-14d)</h5>
                <h2>{warningCount}</h2>
                <small>nesta pagina</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-3">
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar por SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={orderBy}
            onChange={(e) => handleOrderChange(e.target.value)}
          >
            <option value="stockout_days_7d_asc">Menor Stockout 7d</option>
            <option value="stockout_days_28d_asc">Menor Stockout 28d</option>
            <option value="revenue_28d_desc">Maior Receita 28d</option>
            <option value="revenue_7d_desc">Maior Receita 7d</option>
            <option value="stock_quantity_asc">Menor Stock</option>
            <option value="sales_potential_desc">Maior Pot. Vendas</option>
            <option value="revenue_pct_28d_desc">Maior % Empresa 28d</option>
            <option value="position_asc">Melhor Posicao</option>
          </select>
        </div>
        <div className="col-md-3">
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeZeroStock"
              checked={includeZeroStock}
              onChange={(e) => setIncludeZeroStock(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="includeZeroStock">
              Incluir sem stock
            </label>
          </div>
        </div>
        <div className="col-md-3 text-end">
          <span className="text-muted">{totalCount} produtos</span>
        </div>
      </div>

      {/* Error */}
      {error && <div className="alert alert-warning">{error}</div>}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="mt-2 text-muted">A carregar dados (pode demorar 10-15s)...</p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && forecastData.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover" style={{ fontSize: '0.82rem' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '40px' }}>Pos</th>
                  <th style={{ width: '90px' }}>SKU</th>
                  <th style={{ minWidth: '280px' }}>Produto</th>
                  <th style={{ width: '60px' }} className="text-end">Stock</th>
                  <th style={{ width: '75px' }} className="text-end">Preco</th>
                  <th style={{ width: '55px' }} className="text-center" title="Media dos dias ate stockout">Avg</th>
                  <th style={{ width: '50px' }} className="text-center">28d</th>
                  <th style={{ width: '50px' }} className="text-center">14d</th>
                  <th style={{ width: '50px' }} className="text-center">7d</th>
                  <th style={{ width: '50px' }} className="text-center">3d</th>
                  <th style={{ width: '50px' }} className="text-center">1d</th>
                  <th style={{ width: '50px' }} className="text-end">28d</th>
                  <th style={{ width: '50px' }} className="text-end">14d</th>
                  <th style={{ width: '50px' }} className="text-end">7d</th>
                  <th style={{ width: '45px' }} className="text-end">3d</th>
                  <th style={{ width: '45px' }} className="text-end">1d</th>
                  <th style={{ width: '95px' }} className="text-end">Pot. Vendas</th>
                  <th style={{ width: '85px' }} className="text-end">Rec. 7d</th>
                  <th style={{ width: '55px' }} className="text-end">% 7d</th>
                  <th style={{ width: '85px' }} className="text-end">Rec. 28d</th>
                  <th style={{ width: '55px' }} className="text-end">% 28d</th>
                </tr>
                <tr>
                  <th colSpan={5}></th>
                  <th colSpan={6} className="text-center" style={{ fontSize: '0.72rem', fontWeight: 'normal', borderBottom: '2px solid #dc3545' }}>
                    Dias ate Stockout
                  </th>
                  <th colSpan={5} className="text-center" style={{ fontSize: '0.72rem', fontWeight: 'normal', borderBottom: '2px solid #0d6efd' }}>
                    Unidades Vendidas
                  </th>
                  <th colSpan={5}></th>
                </tr>
              </thead>
              <tbody>
                {forecastData.map((item) => {
                  const avgStockout = calcAvgStockout(item);
                  return (
                    <tr key={item.sku}>
                      <td className="text-center">{item.ranking_position || '-'}</td>
                      <td>
                        <button
                          className="btn btn-link btn-sm p-0 text-primary"
                          onClick={() => handleSkuClick(item.sku)}
                          title="Ver detalhes"
                        >
                          {item.sku}
                        </button>
                      </td>
                      <td>
                        <span className="text-truncate d-inline-block" style={{ maxWidth: '350px' }} title={item.product_name || ''}>
                          {item.product_name || '-'}
                        </span>
                      </td>
                      <td className="text-end">{item.stock_quantity}</td>
                      <td className="text-end">{item.preco_atual ? `${item.preco_atual.toFixed(2)}\u20AC` : '-'}</td>
                      <td className="text-center">{formatStockoutDays(avgStockout)}</td>
                      <td className="text-center">{formatStockoutDays(item.stockout_days_28d)}</td>
                      <td className="text-center">{formatStockoutDays(item.stockout_days_14d)}</td>
                      <td className="text-center">{formatStockoutDays(item.stockout_days_7d)}</td>
                      <td className="text-center">{formatStockoutDays(item.stockout_days_3d)}</td>
                      <td className="text-center">{formatStockoutDays(item.stockout_days_1d)}</td>
                      <td className="text-end">{calcUnitsSold(item.dpd_28, 28)}</td>
                      <td className="text-end">{calcUnitsSold(item.dpd_14, 14)}</td>
                      <td className="text-end">{calcUnitsSold(item.dpd_7, 7)}</td>
                      <td className="text-end">{calcUnitsSold(item.dpd_3, 3)}</td>
                      <td className="text-end">{item.dpd_1 !== null ? (item.dpd_1 < 1 ? item.dpd_1.toFixed(1) : Math.round(item.dpd_1).toString()) : '-'}</td>
                      <td className="text-end">{formatEur(item.sales_potential)}</td>
                      <td className="text-end">{formatEur(item.revenue_7d)}</td>
                      <td className="text-end">{formatPct(item.revenue_pct_7d)}</td>
                      <td className="text-end">{formatEur(item.revenue_28d)}</td>
                      <td className="text-end">{formatPct(item.revenue_pct_28d)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-3">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    Primeira
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                    Anterior
                  </button>
                </li>
                <li className="page-item active">
                  <span className="page-link">Pagina {currentPage} de {totalPages}</span>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                    Proxima
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                    Ultima
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !error && forecastData.length === 0 && (
        <div className="alert alert-info text-center">
          Nenhum produto encontrado.
        </div>
      )}
    </div>
  );
};

export default StockForecastPage;
